import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { titleCase } from '../../lib/format'
import type { ListResponse, Organization, Site } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select, Skeleton,
  StatusBadge, Textarea,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { CloseIcon, IdIcon, PlusIcon } from '../../components/icons'

/* Platform-wide site register — the ONLY place a mining site is created.
 *
 * A site is a claim over land, so granting one is an administrator's
 * decision rather than something a cooperative does for itself: POST /sites
 * is super-admin-only and takes the receiving `org_id` in the body (see
 * api/routes.py:api_create_site). Cooperatives and companies see their
 * allocated sites read-only on their own Sites page. */

const MINERALS = [
  'Gold', 'Silver', 'Diamond', 'Copper', 'Bauxite', 'Iron Ore',
  'Manganese', 'Chromite', 'Coltan', 'Lithium', 'Cobalt', 'Other',
]
const LAND_TENURE_TYPES = ['informal', 'customary', 'leasehold', 'freehold', 'state']

export default function AdminSites() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [view, setView] = useState<'summary' | 'table'>('summary')
  const [minerals, setMinerals] = useState<string[]>([])

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'sites'],
    queryFn: () => api.get<ListResponse<Site>>('/sites'),
  })
  const orgsQuery = useQuery({
    queryKey: ['admin', 'orgs-for-sites'],
    queryFn: () => api.get<{ organizations: Organization[] }>('/organizations?limit=200'),
  })

  const allocate = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<{ site: Site }>('/sites', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'sites'] })
      setShowForm(false)
      setMinerals([])
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const num = (k: string) => {
      const v = form.get(k)
      return v ? Number(v) : undefined
    }
    allocate.mutate({
      org_id: String(form.get('org_id') ?? ''),
      name: String(form.get('name') ?? '').trim(),
      mineral_types: minerals,
      land_tenure_type: String(form.get('land_tenure_type') ?? 'informal'),
      region_id: String(form.get('region_id') ?? '').trim(),
      district_id: String(form.get('district_id') ?? '').trim(),
      village: String(form.get('village') ?? '').trim(),
      description: String(form.get('description') ?? '').trim(),
      latitude: num('latitude'),
      longitude: num('longitude'),
      area_size_ha: num('area_size_ha'),
    })
  }

  const sites = data?.items ?? []
  const orgs = orgsQuery.data?.organizations ?? []
  const orgName = (id?: string) => orgs.find((o) => o.id === id)?.name || id || '—'

  const byOrg = (() => {
    const totals = new Map<string, number>()
    for (const s of sites) totals.set(s.org_id || '', (totals.get(s.org_id || '') ?? 0) + 1)
    return [...totals.entries()].sort((a, b) => b[1] - a[1])
  })()
  const validated = sites.filter((s) => s.validation_status === 'validated').length
  const pending = sites.length - validated

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Mining sites</h1>
          <p className="page-head__sub">
            Every site on the platform. Sites are allocated here and to one organisation —
            cooperatives and companies cannot register their own.
          </p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Allocate site'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'Allocate site'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {isError && <Alert tone="danger">{(error as Error).message}</Alert>}

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Allocate a site" sub="Choose the organisation that will hold it" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {allocate.isError && (
                  <Alert tone="danger">
                    {allocate.error instanceof ApiError ? allocate.error.message : 'Could not allocate that site.'}
                  </Alert>
                )}
                <Wizard
                  steps={[
                    { title: 'Who holds it', content: (
                      <Select label="Organisation" name="org_id" required defaultValue="">
                        <option value="" disabled>Select an organisation…</option>
                        {orgs.map((o) => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </Select>
                    ) },
                    { title: 'Site & tenure', content: (
                      <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                        <Input label="Site name" name="name" required />
                        <Select label="Land tenure" name="land_tenure_type" defaultValue="informal">
                          {LAND_TENURE_TYPES.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
                        </Select>
                      </div>
                    ) },
                    { title: 'Minerals', content: (
                      <div>
                        <label className="field__label" style={{ display: 'block', marginBottom: 6 }}>Minerals</label>
                        <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                          {MINERALS.map((m) => (
                            <label key={m} className={`radio-opt${minerals.includes(m) ? ' radio-opt--checked' : ''}`}
                                   style={{ padding: 'var(--sp-2) var(--sp-3)' }}>
                              <input
                                type="checkbox"
                                checked={minerals.includes(m)}
                                onChange={() => setMinerals((cur) => (
                                  cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]
                                ))}
                              />
                              {m}
                            </label>
                          ))}
                        </div>
                      </div>
                    ) },
                    { title: 'Location', content: (
                      <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                        <div className="grid grid--2">
                          <Input label="Province / region" name="region_id" />
                          <Input label="District" name="district_id" />
                        </div>
                        <Input label="Village / area (optional)" name="village" />
                      </div>
                    ) },
                    { title: 'Coordinates & size', content: (
                      <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                        <div className="grid grid--2">
                          <Input label="Latitude (optional)" name="latitude" type="number" step="any" />
                          <Input label="Longitude (optional)" name="longitude" type="number" step="any" />
                        </div>
                        <Input label="Area (hectares, optional)" name="area_size_ha" type="number" step="any" min="0" />
                      </div>
                    ) },
                    { title: 'Notes', content: (
                      <Textarea label="Description (optional)" name="description" rows={3} />
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={allocate.isPending} confirm={{ title: "Allocate this site?", message: "The organisation you chose becomes its holder." }}>Allocate site</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button variant={view === 'summary' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('summary')}>
          Summary
        </Button>
        <Button variant={view === 'table' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('table')}>
          Table
        </Button>
      </div>

      {view === 'summary' && !isLoading && (
        <div className="stack" style={{ gap: 'var(--sp-5)', marginBottom: 'var(--sp-6)' }}>
          <div className="grid grid--tiles-3">
            <Card><div className="stat">
              <div className="stat__top"><span className="stat__label">Sites allocated</span></div>
              <div className="stat__value">{sites.length}</div>
            </div></Card>
            <Card><div className="stat">
              <div className="stat__top"><span className="stat__label">Validated</span></div>
              <div className="stat__value">{validated}</div>
            </div></Card>
            <Card><div className="stat">
              <div className="stat__top"><span className="stat__label">Awaiting validation</span></div>
              <div className="stat__value">{pending}</div>
            </div></Card>
          </div>
          <Card>
            <CardHeader title="By organisation" sub="Sites held, most first" />
            <CardBody>
              {byOrg.length === 0 ? (
                <EmptyState icon={<IdIcon size={22} />} title="No sites allocated yet"
                            body="Use “Allocate site” to grant an organisation its first site." />
              ) : (
                <div className="stack" style={{ gap: 'var(--sp-3)' }}>
                  {byOrg.map(([id, count]) => (
                    <div key={id}>
                      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 'var(--text-sm)' }}>{orgName(id)}</span>
                        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700 }}>{count}</span>
                      </div>
                      <div style={{ height: 8, background: 'var(--accent-soft)', borderRadius: 4 }}>
                        <div style={{
                          height: '100%', borderRadius: 4, background: 'var(--accent)',
                          width: `${Math.max(4, Math.round((count / (byOrg[0]?.[1] || 1)) * 100))}%`,
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {view === 'table' && (
        <Card>
          <CardHeader title="Sites" sub={sites.length ? `${sites.length} allocated` : undefined} />
          <CardBody flush>
            {isLoading ? (
              <div style={{ padding: 'var(--sp-6)' }} className="stack">
                <Skeleton h={20} /><Skeleton h={20} w="70%" />
              </div>
            ) : sites.length === 0 ? (
              <EmptyState icon={<IdIcon size={22} />} title="No sites allocated yet"
                          body="Use “Allocate site” to grant an organisation its first site." />
            ) : (
              <>
                <div className="mobile-only">
                  {sites.map((s) => (
                    <Link key={s.id} to={`/sites/${s.id}`} className="lrow">
                      <div className="lrow__main">
                        <div className="lrow__title">{s.name}</div>
                        <div className="lrow__sub">
                          {orgName(s.org_id)} · {[titleCase(s.district_id), titleCase(s.region_id)]
                            .filter(Boolean).join(', ') || 'Location not set'}
                        </div>
                      </div>
                      <div className="lrow__end">
                        <StatusBadge status={s.validation_status || 'pending'} />
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="table-wrap desktop-only">
                  <table className="table">
                    <thead>
                      <tr><th>Site</th><th>Organisation</th><th>Location</th><th>Minerals</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {sites.map((s) => (
                        <tr key={s.id}>
                          <td><Link to={`/sites/${s.id}`} style={{ fontWeight: 600 }}>{s.name}</Link></td>
                          <td>{orgName(s.org_id)}</td>
                          <td>{[titleCase(s.district_id), titleCase(s.region_id)].filter(Boolean).join(', ') || '—'}</td>
                          <td>{(s.mineral_types ?? []).map(titleCase).join(', ') || '—'}</td>
                          <td><StatusBadge status={s.validation_status || 'pending'} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      )}
    </>
  )
}
